import { useState, useEffect } from "react";
import Select from "react-select";
import axios from "axios";
import Lf23Loader from "../../../components/Lf23Loader";

const ANNO_DEFAULT = 2019;

// separatore delle migliaia forzato: in it-IT il browser non lo mette sui numeri a 4 cifre (1955 invece di 1.955)
const formatKm = (n) => String(Math.round(n)).replace(/\B(?=(\d{3})+(?!\d))/g, '.');
const formatPerc = (n) => `${n.toLocaleString('it-IT', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;
const formatData = (iso) => iso.split('-').reverse().join('/');
const formatMezzi = (n) => (n === 1 ? '1 mezzo' : `${n} mezzi`);
const formatTarghe = (n) => (n === 1 ? '1 targa' : `${n} targhe`);

// codici "motivo" restituiti dal backend per le targhe senza data di immatricolazione
const MOTIVI_ND = {
    gps_senza_targa: {
        label: 'GPS senza targa',
        descrizione: "Non è una targa ma il numero di serie del dispositivo GPS: Webfleet usa questo nome quando il box non è abbinato a un mezzo targato"
    },
    targa_non_in_anagrafica: {
        label: 'Targa non in anagrafica',
        descrizione: "È una targa, ma il mezzo non è (più) presente nell'anagrafica automezzi"
    },
    data_mancante: {
        label: 'Data mancante',
        descrizione: "Il mezzo è in anagrafica ma senza data di immatricolazione"
    }
};

// Tabella "Km percorsi per anno di immatricolazione": ripartisce i km del periodo
// tra mezzi immatricolati dall'anno scelto in poi, prima e senza data.
// filterParams = querystring dei filtri del tab (stessa di buildFilterParams in Generale.js)
const KmPerImmatricolazione = ({ filterParams }) => {
    const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3001/api";

    const [anniOptions, setAnniOptions] = useState([]);
    const [annoSoglia, setAnnoSoglia] = useState(ANNO_DEFAULT);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [errore, setErrore] = useState(false);
    const [showDettaglio, setShowDettaglio] = useState(false);

    // anni disponibili per il selettore (una sola volta)
    useEffect(() => {
        axios.get(`${API_URL}/operation-automezzi/generale/anni-immatricolazione`)
            .then(res => setAnniOptions(res.data.map(a => ({ value: a, label: String(a) }))))
            .catch(error => console.error("Errore nel caricamento anni di immatricolazione:", error));
    }, [API_URL]);

    // dati della tabella: si ricaricano quando cambiano i filtri o l'anno
    useEffect(() => {
        // se l'utente cambia anno velocemente, ignoro le risposte arrivate in ritardo
        let annullato = false;

        const fetchData = async () => {
            setLoading(true);
            setErrore(false);
            try {
                const params = new URLSearchParams(filterParams);
                params.set('anno', annoSoglia);
                const res = await axios.get(`${API_URL}/operation-automezzi/generale/km-immatricolazione?${params.toString()}`);
                if (!annullato) setData(res.data);
            } catch (error) {
                console.error("Errore nel caricamento km per anno di immatricolazione:", error);
                if (!annullato) setErrore(true);
            } finally {
                if (!annullato) setLoading(false);
            }
        };

        fetchData();
        return () => { annullato = true; };
    }, [API_URL, filterParams, annoSoglia]);

    // etichette costruite sull'anno dei dati ricevuti (non su quello appena selezionato)
    const anno = data?.anno ?? annoSoglia;
    const etichette = {
        da_anno: `Immatricolati dal ${anno} in poi`,
        prima_anno: `Immatricolati prima del ${anno}`,
        non_disponibile: 'Data di immatricolazione non disponibile'
    };

    const totaleMezzi = data ? data.totale.reduce((s, r) => s + r.num_mezzi, 0) : 0;
    const kmEsclusi = data ? data.esclusiGps.reduce((s, r) => s + r.km_esclusi, 0) : 0;
    const kmNonDisponibili = data ? data.nonDisponibili.reduce((s, r) => s + r.km, 0) : 0;

    return (
        <div className="automezzi-gen-km-imm">
            <div className="automezzi-gen-km-imm-header">
                <div>
                    <span className="charts-title">Km percorsi per anno di immatricolazione</span>
                    <p className="automezzi-gen-km-imm-subtitle">
                        Ripartizione dei km percorsi nel periodo selezionato tra i mezzi immatricolati
                        dal {anno} in poi e quelli immatricolati prima.
                    </p>
                </div>
                <div className="automezzi-gen-km-imm-anno">
                    <label htmlFor="km-imm-anno">Anno di immatricolazione di riferimento</label>
                    <Select
                        inputId="km-imm-anno"
                        classNamePrefix="oper-hr-select"
                        value={{ value: annoSoglia, label: String(annoSoglia) }}
                        options={anniOptions}
                        onChange={(opt) => setAnnoSoglia(opt.value)}
                        isSearchable={false}
                    />
                </div>
            </div>

            {errore && (
                <p className="automezzi-gen-km-imm-msg">Impossibile caricare i dati. Riprova più tardi.</p>
            )}
            {/* primo caricamento: nessun dato ancora da mostrare */}
            {!errore && !data && loading && <Lf23Loader />}
            {!errore && data && data.kmTotali === 0 && (
                <p className="automezzi-gen-km-imm-msg">Nessun km percorso nel periodo con i filtri selezionati.</p>
            )}

            {!errore && data && data.kmTotali > 0 && (
                <div className="automezzi-gen-km-imm-body">
                    {/* ricaricamento (cambio anno o filtri): loader sopra i dati vecchi */}
                    {loading && (
                        <div className="automezzi-gen-km-imm-loading-overlay">
                            <Lf23Loader testo={null} />
                        </div>
                    )}
                    {/* Risposta a colpo d'occhio: barra 100% dei km della flotta */}
                    <div className="automezzi-gen-km-imm-hero">
                        <div className="automezzi-gen-km-imm-stacked grande">
                            {data.totale.filter(r => r.km > 0).map(r => (
                                <div
                                    key={r.classe}
                                    className={`seg seg-${r.classe}`}
                                    style={{ flexGrow: r.km }}
                                    title={`${etichette[r.classe]}: ${formatPerc(r.percentuale)}`}
                                >
                                    {r.percentuale >= 8 && <span>{formatPerc(r.percentuale)}</span>}
                                </div>
                            ))}
                        </div>

                        <div className="automezzi-gen-km-imm-legenda">
                            {data.totale.map(r => (
                                <div key={r.classe} className="voce">
                                    <span className={`dot dot-${r.classe}`} />
                                    <div>
                                        <div className="etichetta">
                                            {etichette[r.classe]}
                                            {r.classe === 'non_disponibile' && (
                                                <span
                                                    className="automezzi-gen-km-imm-info"
                                                    title="Targhe senza data di immatricolazione: l'elenco è in «Dati non classificati ed esclusi» in fondo"
                                                >
                                                    i
                                                </span>
                                            )}
                                        </div>
                                        <div className="valore">{formatPerc(r.percentuale)}</div>
                                        <div className="dettaglio">{formatKm(r.km)} km · {formatMezzi(r.num_mezzi)}</div>
                                    </div>
                                </div>
                            ))}
                            <div className="voce totale">
                                <div>
                                    <div className="etichetta">Totale flotta</div>
                                    <div className="valore">{formatKm(data.kmTotali)} km</div>
                                    <div className="dettaglio">{formatMezzi(totaleMezzi)}</div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Dettaglio per tipologia */}
                    <h5 className="automezzi-gen-km-imm-section-title">Dettaglio per tipologia</h5>
                    <div className="automezzi-gen-km-imm-table-wrapper">
                        <table className="automezzi-gen-km-imm-table">
                            <thead>
                                <tr>
                                    <th className="col-testo">Tipologia</th>
                                    <th>Km</th>
                                    <th><span className="dot dot-da_anno" />Dal {anno}</th>
                                    <th><span className="dot dot-prima_anno" />Prima del {anno}</th>
                                    <th><span className="dot dot-non_disponibile" />N.D.</th>
                                    <th className="col-barra">Ripartizione</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.perTipologia.map(r => (
                                    <tr key={r.tipologia}>
                                        <td className="col-testo">{r.tipologia}</td>
                                        <td>{formatKm(r.km)}</td>
                                        <td>{formatPerc(r.perc_da_anno)}</td>
                                        <td>{formatPerc(r.perc_prima_anno)}</td>
                                        <td>{formatPerc(r.perc_non_disponibile)}</td>
                                        <td className="col-barra">
                                            <div className="automezzi-gen-km-imm-stacked piccola">
                                                <div className="seg seg-da_anno" style={{ flexGrow: r.perc_da_anno }} />
                                                <div className="seg seg-prima_anno" style={{ flexGrow: r.perc_prima_anno }} />
                                                <div className="seg seg-non_disponibile" style={{ flexGrow: r.perc_non_disponibile }} />
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Trasparenza: cosa c'è nella riga N.D. e cosa abbiamo scartato */}
                    <div className={`automezzi-gen-km-imm-accordion${showDettaglio ? ' aperto' : ''}`}>
                        <button
                            type="button"
                            className="automezzi-gen-km-imm-accordion-header"
                            onClick={() => setShowDettaglio(!showDettaglio)}
                            aria-expanded={showDettaglio}
                        >
                            <svg className="chevron" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="9 6 15 12 9 18"></polyline>
                            </svg>
                            <div className="titoli">
                                <span className="titolo">Dati non classificati ed esclusi</span>
                                <span className="sottotitolo">Cosa c'è dietro ai numeri: targhe senza anno e viaggi scartati</span>
                            </div>
                            <div className="pillole">
                                <span className="pillola pillola-inclusi">
                                    {formatTarghe(data.nonDisponibili.length)} · {formatKm(kmNonDisponibili)} km inclusi
                                </span>
                                <span className="pillola pillola-esclusi">
                                    {formatTarghe(data.esclusiGps.length)} · {formatKm(kmEsclusi)} km esclusi
                                </span>
                            </div>
                        </button>

                        {showDettaglio && (
                            <div className="automezzi-gen-km-imm-dettaglio">
                                {/* Targhe senza data: km contati nel totale */}
                                <div className="pannello pannello-inclusi">
                                    <div className="pannello-header">
                                        <h5>Targhe senza data di immatricolazione</h5>
                                        <span className="badge badge-inclusi">Inclusi nel totale</span>
                                    </div>
                                    <p className="nota">
                                        I loro km sono contati nel gruppo «{etichette.non_disponibile}»: senza una targa
                                        abbinata in anagrafica non sappiamo in quale fascia di età metterli. Passa sul motivo
                                        per la spiegazione; la nota è quella scritta sul mezzo in Webfleet.
                                    </p>
                                    {data.nonDisponibili.length === 0 ? (
                                        <p className="vuoto">Nessuna targa senza data nel periodo.</p>
                                    ) : (
                                        <div className="automezzi-gen-km-imm-table-wrapper">
                                            <table className="automezzi-gen-km-imm-table">
                                                <thead>
                                                    <tr>
                                                        <th className="col-testo">Targa</th>
                                                        <th className="col-testo">Motivo</th>
                                                        <th className="col-testo">Nota Webfleet</th>
                                                        <th>Km</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {data.nonDisponibili.map(r => (
                                                        <tr key={r.targa}>
                                                            <td className="col-testo targa">{r.targa}</td>
                                                            <td className="col-testo">
                                                                <span className="motivo" title={MOTIVI_ND[r.motivo]?.descrizione}>
                                                                    {MOTIVI_ND[r.motivo]?.label || r.motivo}
                                                                </span>
                                                            </td>
                                                            <td className="col-testo nota-webfleet">{r.nota_webfleet || '—'}</td>
                                                            <td>{formatKm(r.km)}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </div>

                                {/* Errori GPS: km tolti dal totale */}
                                <div className="pannello pannello-esclusi">
                                    <div className="pannello-header">
                                        <h5>Viaggi esclusi per errore GPS</h5>
                                        <span className="badge badge-esclusi">Esclusi dal totale</span>
                                    </div>
                                    <p className="nota">
                                        Viaggi oltre {data.kmMinErroreGps} km percorsi a una velocità media superiore
                                        a {data.velocitaMaxKmh} km/h (es. migliaia di km in pochi minuti): sono errori di
                                        rilevazione e non vengono conteggiati.
                                    </p>
                                    {data.esclusiGps.length === 0 ? (
                                        <p className="vuoto">Nessun viaggio escluso nel periodo.</p>
                                    ) : (
                                        <div className="automezzi-gen-km-imm-table-wrapper">
                                            <table className="automezzi-gen-km-imm-table">
                                                <thead>
                                                    <tr>
                                                        <th className="col-testo">Targa</th>
                                                        <th className="col-testo">Periodo</th>
                                                        <th>Viaggi</th>
                                                        <th className="col-testo" title="Km e durata mediani dei viaggi esclusi">Viaggio tipo</th>
                                                        <th>Km esclusi</th>
                                                        <th>Vel. media max</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {data.esclusiGps.map(r => (
                                                        <tr key={r.targa}>
                                                            <td className="col-testo targa">
                                                                {r.targa}
                                                                <span className="anno-imm">
                                                                    imm. {r.anno_immatricolazione ?? 'N.D.'}
                                                                </span>
                                                            </td>
                                                            <td className="col-testo">
                                                                {r.primo_giorno === r.ultimo_giorno
                                                                    ? formatData(r.primo_giorno)
                                                                    : `${formatData(r.primo_giorno)} – ${formatData(r.ultimo_giorno)}`}
                                                            </td>
                                                            <td>{r.num_viaggi}</td>
                                                            <td className="col-testo viaggio-tipo">
                                                                {r.num_viaggi > 1 && '≈ '}
                                                                {formatKm(r.km_viaggio_tipo)} km in {r.minuti_viaggio_tipo < 1 ? '<1' : r.minuti_viaggio_tipo} min
                                                            </td>
                                                            <td className="km-esclusi">{formatKm(r.km_esclusi)}</td>
                                                            <td>{formatKm(r.velocita_max_kmh)} km/h</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default KmPerImmatricolazione;
